/**
 * This custom hook provides the functionality to retrieve users to be shown in the UI.
 */

import React, {useContext, useEffect, useState} from "react";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import {useSnackbar} from "notistack";
import {useConfirm} from "material-ui-confirm";
import CourseCard from "../../courses/card/course/CourseCard";
import {useIntl} from "react-intl";

const useListPlaylists = () => {

    // State storage of the playlist shown in the table
    const [playlists, setPlaylists] = useState([])

    const [loading, setLoading] = useState(true)
    const [deletionInProgress, setDeletionInProgress] = useState(false)
    const [deletionAttemptedPlaylist, setDeletionAttemptedPlaylist] = useState(null)

    const {signedInUser} = useContext(FirebaseAuthContext);

    const {enqueueSnackbar} = useSnackbar();
    const confirm = useConfirm();

    // Internationalization for simple strings
    const intl = useIntl();

    // Retrieving the playlists and setting the list in the state variable
    const fetchPlaylists = async () => {
        const idToken = await signedInUser.getIdToken()

        return fetch('/reg-main/playlists?idToken=' + idToken)
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                const playlistsJson = await data.json();
                setPlaylists(playlistsJson)
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.playlists.messages.list.error',
                        defaultMessage: 'Something went wrong while listing the playlists: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
            .finally(() => {
                setLoading(false)
            })
    }

    useEffect(() => {
        fetchPlaylists();
    }, [])

    const handleDeletePlaylist = (playlist) => {
        setDeletionAttemptedPlaylist(playlist.key)
        return checkDeletionAllowed(playlist)
            .then((checkResult) => {
                setDeletionAttemptedPlaylist(null)
                if (checkResult.allowed) {
                    return confirm({
                        title: intl.formatMessage({
                            id: 'app.playlists.dialog.delete.confirm.title',
                            defaultMessage: 'Delete Playlist'
                        }),
                        description: intl.formatMessage({
                            id: 'app.playlists.dialog.delete.confirm.description',
                            defaultMessage: 'Delete playlist {name}?'
                        }, {name: playlist.name})
                    })
                        .then(() => 'OK')
                        .catch(() => null)
                } else {
                    return confirm({
                        title: intl.formatMessage({
                            id: 'app.playlists.dialog.delete.existingReferences.title',
                            defaultMessage: 'Deletion not allowed'
                        }),
                        content: <div>
                            <p>{intl.formatMessage({
                                id: 'app.playlists.dialog.delete.existingReferences.description',
                                defaultMessage: 'The playlist is included in the following courses'
                            })}:</p>
                            <div>
                                {checkResult.courses.map((course) => <CourseCard
                                    key={course.key}
                                    course={course}
                                    textSize={12}
                                    headerSize={14}/>)}
                            </div>
                        </div>
                    }).then(() => null).catch(() => null)
                }
            })
            .then((clickedButton) => {
                if (clickedButton === 'OK') {
                    return deletePlaylist(playlist)
                }
                return null
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.playlists.messages.delete.error',
                        defaultMessage: 'Something went wrong while deleting the playlist: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
            .finally(() => {
                setDeletionAttemptedPlaylist(null)
                setDeletionInProgress(false)
            })
    }

    const checkDeletionAllowed = async (playlist) => {
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/playlists/' + playlist.key + '/deletionAllowed?idToken=' + idToken)
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }
                return data.json();
            })
    }

    const deletePlaylist = async (playlist) => {
        setDeletionInProgress(true)
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/playlists/' + playlist.key, {
            method: 'DELETE',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                idToken: idToken
            })
        }).then(async (data) => {
            // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
            if (data.status !== 200) {
                throw new Error((await data.json()).status)
            }

            await fetchPlaylists()
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'app.playlists.messages.delete.success',
                    defaultMessage: 'Playlist {name} deleted'
                }, {name: playlist.name}), {
                    variant: 'success',
                    preventDuplicate: true,
                    autoHideDuration: 3000
                });
        })
    }

    return {playlists, loading, handleDeletePlaylist, deletionInProgress, deletionAttemptedPlaylist}
}
export default useListPlaylists;